import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { cn } from '../../../../shared';
import { InlineError } from './BookingPanels';
import {
  MAX_NOTE_LENGTH,
  MIN_NOTE_LENGTH,
  OTHER,
  type ReasonOption,
} from './reasons';

/**
 * Asks why, before a cancellation or a move goes through.
 *
 * A fixed list plus "Other": preset answers are comparable across candidates,
 * and the free-text field means nobody has to pick a wrong option. The note is
 * required only for "Other" — elsewhere it is optional detail.
 */
export function ReasonStep({
  heading,
  subline,
  confirmLabel,
  options,
  busy,
  error,
  onBack,
  onConfirm,
}: {
  heading: string;
  subline?: string;
  confirmLabel: string;
  options: ReasonOption[];
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onConfirm: (payload: { reasonCode: string; note: string }) => void;
}) {
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');

  const trimmed = note.trim();
  const needsNote = code === OTHER;
  const noteTooLong = trimmed.length > MAX_NOTE_LENGTH;
  const canSubmit =
    code !== '' && !noteTooLong && (!needsNote || trimmed.length >= MIN_NOTE_LENGTH);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit && !busy) onConfirm({ reasonCode: code, note: trimmed });
      }}
    >
      <Button size="sm" variant="ghost" type="button" onClick={onBack} className="mb-3">
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Back
      </Button>

      <h3 className="text-sm font-semibold text-gray-900">{heading}</h3>
      {subline ? <p className="mt-1 text-sm text-gray-600">{subline}</p> : null}

      <fieldset className="mt-3">
        <legend className="mb-1.5 text-xs font-medium text-gray-700">
          Reason <span className="text-red-600">*</span>
        </legend>
        <div className="space-y-1.5">
          {options.map((o) => (
            <label
              key={o.code}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 text-sm',
                code === o.code
                  ? 'border-gray-900 bg-gray-50 text-gray-900'
                  : 'border-gray-200 text-gray-700 hover:border-gray-400'
              )}
            >
              <input
                type="radio"
                name="reason"
                value={o.code}
                checked={code === o.code}
                onChange={() => setCode(o.code)}
                className="mt-0.5 h-4 w-4"
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {code !== '' ? (
        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-medium text-gray-700">
            {needsNote ? (
              <>
                Please tell us more <span className="text-red-600">*</span>
              </>
            ) : (
              'Anything to add? (optional)'
            )}
          </span>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={needsNote ? 'A sentence is enough' : 'Optional'}
            rows={3}
            maxLength={MAX_NOTE_LENGTH}
          />
          <span className="mt-1 block text-xs text-gray-500">This goes to the hiring team.</span>
        </label>
      ) : null}

      {error ? <InlineError message={error} /> : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={!canSubmit || busy}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
          {confirmLabel}
        </Button>
        {!canSubmit ? (
          <span className="text-xs text-gray-500">
            {code === ''
              ? 'Please choose a reason.'
              : needsNote
                ? 'Please add a short explanation.'
                : 'That note is too long.'}
          </span>
        ) : null}
      </div>
    </form>
  );
}
